import { NotificationCenterEmptyState } from "@vega-core/components/NotificationCenter/components/NotificationCenterEmptyState";
import { NotificationCenterError } from "@vega-core/components/NotificationCenter/components/NotificationCenterError";
import { NotificationCenterItem } from "@vega-core/components/NotificationCenter/components/NotificationCenterItem";
import { NotificationCenterLoadMoreSentinel } from "@vega-core/components/NotificationCenter/components/NotificationCenterLoadMoreSentinel";
import { NotificationCenterSkeleton } from "@vega-core/components/NotificationCenter/components/NotificationCenterSkeleton";
import { useNotificationCenter } from "@vega-core/components/NotificationCenter/NotificationCenter.context";

type Props = {
  emptyTitle?: string;
  emptyDescription?: string;
  errorTitle?: string;
  errorDescription?: string;
  retryLabel?: string;
  skeletonCount?: number;
};

/**
 * Page list for the co-invest Notifications page: reuses the shared
 * NotificationCenter items and state leaves, but lays them out as gapped cards
 * that scroll with the whole page (no inner scroll container).
 */
export const CoInvestNotificationsList = ({
  emptyTitle,
  emptyDescription,
  errorTitle,
  errorDescription,
  retryLabel,
  skeletonCount,
}: Props) => {
  const { items, hasMore, isLoadingMore, onLoadMore, isLoading, error } =
    useNotificationCenter();

  if (error && items.length === 0) {
    return (
      <NotificationCenterError
        description={errorDescription}
        retryLabel={retryLabel}
        title={errorTitle}
      />
    );
  }

  if (isLoading && items.length === 0) {
    return <NotificationCenterSkeleton count={skeletonCount} />;
  }

  if (items.length === 0) {
    return (
      <NotificationCenterEmptyState
        description={emptyDescription}
        title={emptyTitle}
      />
    );
  }

  return (
    <>
      <ul
        className="flex flex-col gap-3"
        data-testid="coinvest-notifications-list"
      >
        {items.map((notification) => (
          <li
            className="rounded-lg border border-border/primary overflow-hidden"
            key={notification.id}
          >
            <NotificationCenterItem notification={notification} />
          </li>
        ))}
      </ul>
      {hasMore && onLoadMore ? (
        <NotificationCenterLoadMoreSentinel
          isLoadingMore={Boolean(isLoadingMore)}
          onLoadMore={onLoadMore}
        />
      ) : null}
    </>
  );
};
